import Modifier from 'ember-modifier'
import { service } from '@ember/service'
import type TeacherEvents from 'teacher-ui-client/services/teacher-events'
import { registerDestructor } from '@ember/destroyable'
import type Owner from '@ember/owner'
import type { ArgsFor } from 'ember-modifier'

interface Signature {
  Args: {
    Named: Record<PropertyKey, any>
    Positional: [string, string]
  }
  Element: HTMLElement
}

/**
 * Modifier: teacherEvent
 *
 * Attaches a click event listener to the target element that sends an event
 * to the TeacherEvent service when triggered. The event name, product, and
 * optional event data are passed as arguments.
 *
 * Usage (in template):
 *   <button {{teacherEvent "eventName" "product" someData=data}}>Click me</button>
 *
 * Positional Args:
 *   [0] eventName: string - The name of the event to send.
 *   [1] product: string - The product identifier.
 *
 * Named Args:
 *   eventData: Record<PropertyKey, any> - Additional event data to send.
 *
 * @class teacherTotterEvent
 * @extends Modifier<Signature>
 */

export class teacherEventClick extends Modifier<Signature> {
  @service teacherEvents!: TeacherEvents

  modify(
    element: Signature['Element'],
    [eventName, product]: Signature['Args']['Positional'],
    eventData: Signature['Args']['Named'],
  ) {
    element.addEventListener('click', () => {
      this.teacherEvents.sendEvent(eventName, product, eventData)
    })
  }
}

/**
 * Modifier: teacherEventMouseEnter
 *
 * Sends an event to the TeacherEvent service after the pointer has hovered the
 * target element continuously for 2 seconds. Moving the pointer off the element
 * (mouseleave) before the delay elapses cancels the pending event, so brief
 * pass-overs do not fire. The timer is also cleared on teardown.
 *
 * Usage (in template):
 *   <div {{teacherEventMouseEnter "eventName" "product" someData=data}}></div>
 *
 * Positional Args:
 *   [0] eventName: string - The name of the event to send.
 *   [1] product: string - The product identifier.
 *
 * Named Args:
 *   eventData: Record<PropertyKey, any> - Additional event data to send.
 */

export class teacherEventMouseEnter extends Modifier<Signature> {
  @service teacherEvents!: TeacherEvents

  private cleanup?: () => void

  constructor(owner: Owner, args: ArgsFor<Signature>) {
    super(owner, args)

    // Ensure any pending hover timer/listeners are torn down when the modifier
    // is destroyed, so the timer cannot fire (and look up a destroyed owner's
    // service) after the element has been removed.
    registerDestructor(this, () => this.cleanup?.())
  }

  modify(
    element: Signature['Element'],
    [eventName, product]: Signature['Args']['Positional'],
    eventData: Signature['Args']['Named'],
  ) {
    this.cleanup?.()

    const hoverDelayMs = 2000
    let hoverTimer: ReturnType<typeof setTimeout> | null = null

    const clearHoverTimer = () => {
      if (hoverTimer) {
        clearTimeout(hoverTimer)
        hoverTimer = null
      }
    }

    const onMouseEnter = () => {
      clearHoverTimer()
      hoverTimer = setTimeout(() => {
        this.teacherEvents.sendEvent(eventName, product, eventData)
        hoverTimer = null
      }, hoverDelayMs)
    }

    const onMouseLeave = () => {
      clearHoverTimer()
    }

    element.addEventListener('mouseenter', onMouseEnter)
    element.addEventListener('mouseleave', onMouseLeave)

    this.cleanup = () => {
      clearHoverTimer()
      element.removeEventListener('mouseenter', onMouseEnter)
      element.removeEventListener('mouseleave', onMouseLeave)
    }
  }
}
